import { Button } from '@material-ui/core';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { QuoteStatuses, DialogNames } from '../constants';
import currentDate from '../currentDate';
import useLaunchCurrencyPayWindow from '../launchCurrencyPayWindow';
import { Dispatch, iRootState } from '../redux/store';

const roundDown = function roundDownToTwoDecimalPlaces(price: number): number {
  return Math.floor(price * 100) / 100;
};

const PaySelectedInstallments = function PaySelectedInstallments(): React.ReactElement {
  const { launchCurrencyPayWindow } = useLaunchCurrencyPayWindow();
  const dispatch = useDispatch<Dispatch>();
  const quoteId = useSelector((state: iRootState) => state.quote.id);
  const items = useSelector((state: iRootState) => state.quote.items);
  const tax = useSelector((state: iRootState) => state.quote.tax);
  const useFortis = useSelector(
    (state: iRootState) => state.quote.useFortisPayment
  );
  const freightTotal = useSelector(
    (state: iRootState) => state.quote.freightTotal
  );
  const projectName = useSelector(
    (state: iRootState) => state.quote.projectName
  );
  const selectedPaymentsTotal = useSelector(
    (state: iRootState) => state.paymentTerms.selectedPaymentsTotal
  );
  const selectedTerms = useSelector((state: iRootState) =>
    state.paymentTerms.terms.filter(t => t.checked)
  );
  const orderId = `${quoteId}/${selectedTerms
    .map(st => st.termOrder + 1)
    .join()}`;
  const checkedPercentage = selectedTerms
    .map(t => t.percentPrice)
    .reduce((percentage, percentPrice) => percentage + percentPrice, 0);

  const paymentMethods = useSelector(
    (state: iRootState) => state.currencyPay.config.paymentMethods
  );

  const isDefaultAccount = useSelector(
    (state: iRootState) => state.currencyPay.isDefaultAccount
  );

  const currencyItems = items
    .map(item => ({
      itemId: item.itemId,
      itemDescription: item.description,
      itemPrice: roundDown(item.price * checkedPercentage),
      itemQty: item.quantity,
      um: item.um === 'ea' ? 'each' : item.um,
      productCode: item.productCode
    }))
    .filter(f => f.itemPrice > 0);

  const roundedTax = roundDown(tax * checkedPercentage);
  const roundedShipping = roundDown(freightTotal * checkedPercentage);

  const itemTotal = currencyItems
    .map(i => i.itemPrice * i.itemQty)
    .reduce((total, price) => total + price, 0);
  const differenceBetweenTotalAndSelected =
    selectedPaymentsTotal - itemTotal - roundedTax - roundedShipping;
  const roundedDifference = Number(
    differenceBetweenTotalAndSelected.toFixed(2)
  );

  if (roundedDifference > 0) {
    const roundingItem = {
      itemId: 'PaymentTermsAdjustment',
      itemDescription: 'Payment Terms Adjustment',
      itemPrice: roundedDifference,
      itemQty: 1,
      um: 'each',
      productCode: 'PaymentTermsAdjustment'
    };
    currencyItems.push(roundingItem);
  }
  const status = useSelector((state: iRootState) => state.quote.status);
  const dealerId = useSelector((state: iRootState) => state.quote.dealerId);
  const hasACH = paymentMethods.BankWire;
  const hasСС = paymentMethods.CreditCard;
  const showConfirmation = status !== QuoteStatuses.Accepted;
  const openCurrency = function openCurrency() {
    if (currencyItems.length > 0) {
      launchCurrencyPayWindow(
        selectedPaymentsTotal,
        currencyItems,
        orderId,
        projectName,
        roundedTax,
        roundedShipping,
        useFortis,
        hasACH,
        hasСС,
        dealerId
      );
    }
  };

  const onClick = function handleOnClick() {
    if (showConfirmation) {
      dispatch.quote.setQuoteStatus(QuoteStatuses.Accepted, currentDate());
      dispatch.dialog.setOpenDialog(DialogNames.SendFeedback);
    } else {
      if (!isDefaultAccount) {
        dispatch.currencyPay.setPaymentMethods({
          ...paymentMethods,
          ExpressFinancing: false
        });
      }

      openCurrency();
    }
  };

  const payInstallmentsDisabled =
    selectedTerms.filter(t => t.sellPrice > 0).length === 0;

  return (
    <Button
      color='primary'
      variant='contained'
      onClick={onClick}
      disabled={payInstallmentsDisabled}
      style={{ marginRight: '60px', marginTop: '50px' }}
    >
      <FormattedMessage
        id='payInstallments'
        defaultMessage='Pay Installments'
      />
    </Button>
  );
};

export default PaySelectedInstallments;
